/**
 * Car PDI Checklist Data Model
 * 
 * Each section contains items to check during pre-delivery inspection.
 * Status values: 'pending' | 'pass' | 'fail' | 'attention'
 */

import { FEATURE_BASED_CHECKS } from './modelSpecificChecks';

export const CHECKLIST_SECTIONS = [
  {
    id: 'vehicle-details',
    title: 'Vehicle Details',
    icon: 'FileText',
    description: 'Verify all vehicle identification and documentation',
    critical: true,
    items: [
      {
        id: 'vd-1',
        title: 'VIN Number Match',
        description: 'Check VIN on dashboard, door frame, and documents match exactly',
        tip: 'VIN is typically 17 characters. Compare all three locations carefully.',
        warning: 'Mismatched VIN is a serious red flag - do not accept delivery',
        critical: true,
      },
      {
        id: 'vd-2',
        title: 'Engine Number Match',
        description: 'Verify engine number matches the RC and invoice',
        tip: 'Engine number is usually stamped on the engine block near the cylinder head',
        critical: true,
      },
      {
        id: 'vd-3',
        title: 'Chassis Number',
        description: 'Check chassis number matches all documents',
        tip: 'Usually found stamped on the chassis near the engine bay or under seats',
        critical: true,
      },
      {
        id: 'vd-4',
        title: 'Color & Variant Match',
        description: 'Confirm exterior color and variant matches your order',
        tip: 'Check the color code on door jamb sticker matches invoice',
      },
      {
        id: 'vd-5',
        title: 'Manufacturing Date',
        description: 'Check manufacturing date - should be recent',
        tip: 'Ideally within 3 months. Found on door jamb or VIN plate.',
        warning: 'Older stock may have degraded battery, tyres, or fluids',
      },
      {
        id: 'vd-6',
        title: 'Odometer Reading',
        description: 'Should show minimal km (typically under 50 km)',
        tip: 'Higher readings may indicate test drives or prior delivery attempts',
        warning: 'More than 100 km is suspicious for a new car',
      },
    ],
  },
  {
    id: 'exterior',
    title: 'Exterior Inspection',
    icon: 'Car',
    description: 'Check body panels, paint, glass, and external components',
    items: [
      {
        id: 'ext-1',
        title: 'Paint Quality - All Panels',
        description: 'Inspect paint on all body panels for scratches, swirls, or orange peel',
        tip: 'Use your phone flashlight at an angle to spot imperfections. Check in daylight.',
      },
      {
        id: 'ext-2',
        title: 'Paint Color Consistency',
        description: 'Ensure uniform color across all panels - no mismatched shades',
        tip: 'Compare adjacent panels. Mismatch may indicate repairs or replacement.',
        warning: 'Color mismatch could mean accident/repair',
      },
      {
        id: 'ext-3',
        title: 'Body Panel Gaps',
        description: 'Check all panel gaps are uniform and symmetrical',
        tip: 'Run your finger along gaps. Both sides should feel identical.',
      },
      {
        id: 'ext-4',
        title: 'Dents & Dings',
        description: 'Look for dents, dings, or dimples on all body panels',
        tip: 'View panels at eye level from different angles',
      },
      {
        id: 'ext-5',
        title: 'Windshield & Glass',
        description: 'Check all glass for chips, cracks, scratches, or distortion',
        tip: 'Look through glass at different angles. Check edges for chips.',
        critical: true,
      },
      {
        id: 'ext-6',
        title: 'Headlights & DRLs',
        description: 'Verify both headlights work, no condensation inside',
        tip: 'Check low beam, high beam, and DRL functions',
      },
      {
        id: 'ext-7',
        title: 'Tail Lights & Brake Lights',
        description: 'Test all rear lights including reverse and brake lights',
        tip: 'Ask someone to help verify brake lights while you press the pedal',
      },
      {
        id: 'ext-8',
        title: 'Turn Signals & Hazards',
        description: 'Check all indicators front, side, and rear',
        tip: 'Test hazard switch to verify all indicators blink together',
      },
      {
        id: 'ext-9',
        title: 'Fog Lights (if equipped)',
        description: 'Test front and rear fog lights',
        tip: 'These may require specific switch positions to activate',
      },
      {
        id: 'ext-10',
        title: 'Side Mirrors',
        description: 'Check both mirrors for scratches, adjust electrically if equipped',
        tip: 'Test fold function if available',
      },
      {
        id: 'ext-11',
        title: 'Door Handles & Locks',
        description: 'Test all door handles open smoothly, lock/unlock works',
        tip: 'Try both key fob and manual operation',
      },
      {
        id: 'ext-12',
        title: 'Bumpers & Trim',
        description: 'Check bumpers are properly fitted, no scratches on trim',
        tip: 'Inspect lower bumper areas often damaged during transport',
      },
      {
        id: 'ext-13',
        title: 'Roof & Sunroof',
        description: 'Inspect roof for dents, test sunroof operation if equipped',
        tip: 'Open sunroof fully and check for smooth operation',
      },
      {
        id: 'ext-14',
        title: 'Boot/Trunk Lid',
        description: 'Check boot opens smoothly, hinges work properly',
        tip: 'Test both key fob and interior release button',
      },
      {
        id: 'ext-15',
        title: 'Fuel Flap',
        description: 'Verify fuel flap opens easily, cap present and seals properly',
        tip: 'Check the fuel cap clicks when tightened',
      },
      {
        id: 'ext-16',
        title: 'Wiper Blades',
        description: 'Check front and rear wipers for damage, test spray function',
        tip: 'Wipers should move smoothly without streaking',
      },
    ],
  },
  {
    id: 'interior',
    title: 'Interior Inspection',
    icon: 'Armchair',
    description: 'Examine seats, dashboard, trim, and cabin features',
    items: [
      {
        id: 'int-1',
        title: 'Seats - Condition',
        description: 'Check all seats for tears, stains, or uneven stitching',
        tip: 'Look under seats too for any debris or damage',
      },
      {
        id: 'int-2',
        title: 'Seats - Adjustment',
        description: 'Test all seat adjustments - manual and electric',
        tip: 'Test lumbar support, recline, height, and forward/back movement',
      },
      {
        id: 'int-3',
        title: 'Seat Belts',
        description: 'Check all seat belts retract and lock properly',
        tip: 'Pull sharply to test the locking mechanism',
        critical: true,
      },
      {
        id: 'int-4',
        title: 'Dashboard & Plastics',
        description: 'Inspect dashboard for scratches, proper fit of panels',
        tip: 'Press on panels to check for rattles or loose fittings',
      },
      {
        id: 'int-5',
        title: 'Steering Wheel',
        description: 'Check steering wheel for defects, test tilt/telescopic adjustment',
        tip: 'Verify all steering wheel buttons work',
      },
      {
        id: 'int-6',
        title: 'Pedals',
        description: 'Check accelerator, brake, and clutch pedals feel consistent',
        tip: 'Ensure rubber covers are intact and pedals return smoothly',
      },
      {
        id: 'int-7',
        title: 'Gear Lever / Shifter',
        description: 'Test gear shifting feels smooth and precise',
        tip: 'Check all gear positions engage properly',
      },
      {
        id: 'int-8',
        title: 'Handbrake / Parking Brake',
        description: 'Verify handbrake engages and releases properly',
        tip: 'Test electronic parking brake if equipped',
        critical: true,
      },
      {
        id: 'int-9',
        title: 'Glove Box',
        description: 'Open glove box, check light, damper function',
        tip: 'Should open smoothly with proper resistance',
      },
      {
        id: 'int-10',
        title: 'Center Console Storage',
        description: 'Test all storage compartments, cup holders, armrest',
        tip: 'Check for loose hinges or broken clips',
      },
      {
        id: 'int-11',
        title: 'Interior Lights',
        description: 'Test all interior lights - dome, reading, footwell',
        tip: 'Check automatic door-activated lighting',
      },
      {
        id: 'int-12',
        title: 'Rear View Mirror',
        description: 'Check for chips, test auto-dimming if equipped',
        tip: 'Verify day/night adjustment works',
      },
      {
        id: 'int-13',
        title: 'Sun Visors',
        description: 'Test both sun visors, check vanity mirrors and lights',
        tip: 'Verify they hold position when extended',
      },
      {
        id: 'int-14',
        title: 'Floor Mats',
        description: 'Ensure floor mats are present and properly secured',
        tip: 'Check they don\'t interfere with pedals',
        critical: true,
      },
      {
        id: 'int-15',
        title: 'Boot/Trunk Interior',
        description: 'Check boot lining, carpet, and any trim pieces',
        tip: 'Lift carpet to check spare tyre area',
      },
    ],
  },
  {
    id: 'electricals',
    title: 'Electricals & Infotainment',
    icon: 'Zap',
    description: 'Test all electrical systems and entertainment features',
    items: [
      {
        id: 'elec-1',
        title: 'Battery Health',
        description: 'Verify battery is new and fully charged',
        tip: 'Check manufacturing date on battery. Should be recent.',
      },
      {
        id: 'elec-2',
        title: 'Instrument Cluster',
        description: 'All warning lights should illuminate briefly then turn off',
        tip: 'Note any warning lights that stay on after starting',
        critical: true,
      },
      {
        id: 'elec-3',
        title: 'Infotainment System',
        description: 'Test touchscreen, navigation, and all menus',
        tip: 'Check response time and screen clarity',
      },
      {
        id: 'elec-4',
        title: 'Audio System',
        description: 'Test all speakers with music, check for distortion',
        tip: 'Test at various volumes. Check all speaker zones.',
      },
      {
        id: 'elec-5',
        title: 'Bluetooth Connectivity',
        description: 'Pair your phone, test calls and audio streaming',
        tip: 'Verify phone book sync and call quality',
      },
      {
        id: 'elec-6',
        title: 'USB Ports & Charging',
        description: 'Test all USB ports for charging and data',
        tip: 'Check both front and rear USB ports if available',
      },
      {
        id: 'elec-7',
        title: 'Power Windows',
        description: 'Test all windows - up, down, one-touch, anti-pinch',
        tip: 'Check auto-up/down feature from driver\'s controls',
      },
      {
        id: 'elec-8',
        title: 'Central Locking',
        description: 'Test lock/unlock from key fob, buttons, and door handles',
        tip: 'Verify child lock function on rear doors',
      },
      {
        id: 'elec-9',
        title: 'Air Conditioning',
        description: 'Test AC cooling, heating, blower speeds, and vents',
        tip: 'Let AC run for 5 minutes to check cooling efficiency',
        critical: true,
      },
      {
        id: 'elec-10',
        title: 'Climate Control',
        description: 'Test auto climate, zone controls if equipped',
        tip: 'Verify temperature display accuracy',
      },
      {
        id: 'elec-11',
        title: 'Defogger - Front & Rear',
        description: 'Test windshield and rear window defoggers',
        tip: 'Check that rear defogger lines are intact',
      },
      {
        id: 'elec-12',
        title: 'Horn',
        description: 'Test horn function - should be loud and clear',
        tip: 'Quick tap should produce immediate sound',
        critical: true,
      },
      {
        id: 'elec-13',
        title: 'Reverse Camera / Sensors',
        description: 'Test parking sensors and camera display',
        tip: 'Verify camera guidelines move with steering',
      },
      {
        id: 'elec-14',
        title: 'Keyless Entry & Start',
        description: 'Test proximity unlock and push-button start if equipped',
        tip: 'Try starting without pressing brake to verify safety feature',
      },
      {
        id: 'elec-15',
        title: 'ADAS Features',
        description: 'Verify any advanced driver assistance systems are functional',
        tip: 'Test lane departure, collision warning settings in menu',
      },
    ],
  },
  {
    id: 'engine-bay',
    title: 'Engine Bay',
    icon: 'Cog',
    description: 'Inspect under the hood for leaks and component condition',
    items: [
      {
        id: 'eng-1',
        title: 'Engine Oil Level',
        description: 'Check oil level is between min and max marks',
        tip: 'Engine should be off and cool for accurate reading',
        critical: true,
      },
      {
        id: 'eng-2',
        title: 'Coolant Level',
        description: 'Verify coolant reservoir is at proper level',
        tip: 'Check when engine is cool. Never open radiator cap when hot.',
        critical: true,
        warning: 'Low coolant on new car indicates leak or oversight',
      },
      {
        id: 'eng-3',
        title: 'Brake Fluid Level',
        description: 'Check brake fluid reservoir level',
        tip: 'Should be between MIN and MAX marks',
        critical: true,
      },
      {
        id: 'eng-4',
        title: 'Power Steering Fluid',
        description: 'Check power steering fluid if applicable',
        tip: 'Some modern cars use electric power steering (no fluid)',
      },
      {
        id: 'eng-5',
        title: 'Windshield Washer Fluid',
        description: 'Verify washer fluid reservoir is filled',
        tip: 'Test spray function to confirm',
      },
      {
        id: 'eng-6',
        title: 'Engine Appearance',
        description: 'Check for dust, fingerprints, or damage',
        tip: 'New engine should be clean. Excessive dust may indicate storage.',
      },
      {
        id: 'eng-7',
        title: 'No Visible Leaks',
        description: 'Look for any oil, coolant, or fluid leaks',
        tip: 'Check hose connections and around gaskets',
        critical: true,
      },
      {
        id: 'eng-8',
        title: 'Belts & Hoses',
        description: 'Inspect visible belts and hoses for damage or cracks',
        tip: 'New belts should show no wear or fraying',
      },
      {
        id: 'eng-9',
        title: 'Air Filter',
        description: 'Check air filter is clean and properly installed',
        tip: 'New car air filter should be pristine',
      },
      {
        id: 'eng-10',
        title: 'Hood Struts / Prop',
        description: 'Verify hood stays open on its own',
        tip: 'Should hold firmly without dropping',
      },
    ],
  },
  {
    id: 'tyres-wheels',
    title: 'Tyres & Wheels',
    icon: 'Circle',
    description: 'Check all tyres, wheels, and spare',
    items: [
      {
        id: 'tyre-1',
        title: 'Tyre Brand & Specs',
        description: 'Verify tyre brand and size match documentation',
        tip: 'Check if same brand as promised. Note the DOT date code.',
      },
      {
        id: 'tyre-2',
        title: 'Tyre Manufacturing Date',
        description: 'Check DOT code - tyres should be less than 6 months old',
        tip: 'Last 4 digits show week and year (e.g., 5225 = week 52, 2025)',
        warning: 'Old tyres may have degraded rubber even if unused',
      },
      {
        id: 'tyre-3',
        title: 'Tyre Pressure',
        description: 'Verify all tyres are inflated to recommended pressure',
        tip: 'Check pressure cold. Recommended PSI is on door jamb sticker.',
      },
      {
        id: 'tyre-4',
        title: 'Tread Condition',
        description: 'Check tread depth and pattern on all tyres',
        tip: 'New tyres should have full tread with no flat spots',
      },
      {
        id: 'tyre-5',
        title: 'Sidewall Condition',
        description: 'Inspect sidewalls for bulges, cuts, or damage',
        tip: 'Run your hand around sidewalls to feel for irregularities',
        critical: true,
      },
      {
        id: 'tyre-6',
        title: 'Wheel Condition',
        description: 'Check alloys/steel wheels for scratches or damage',
        tip: 'Inspect wheel edges carefully - common area for curb damage',
      },
      {
        id: 'tyre-7',
        title: 'Wheel Nuts/Bolts',
        description: 'Verify all wheel nuts are present and tight',
        tip: 'Visual check is sufficient - should not be cross-threaded',
      },
      {
        id: 'tyre-8',
        title: 'Spare Tyre',
        description: 'Check spare tyre is present and properly inflated',
        tip: 'Full-size spare or space-saver as per variant',
        critical: true,
      },
      {
        id: 'tyre-9',
        title: 'Jack & Tools',
        description: 'Verify jack, wheel wrench, and tools are present',
        tip: 'Test that jack works and wrench fits wheel nuts',
        critical: true,
      },
      {
        id: 'tyre-10',
        title: 'TPMS (if equipped)',
        description: 'Check tyre pressure monitoring system shows all tyres',
        tip: 'No warning lights should be present',
      },
    ],
  },
  {
    id: 'underbody',
    title: 'Fluids & Underbody',
    icon: 'Droplet',
    description: 'Check underneath the vehicle for any issues',
    items: [
      {
        id: 'under-1',
        title: 'Underbody Coating',
        description: 'Check if underbody protection is applied (if promised)',
        tip: 'Should be even coating without drips or missed spots',
      },
      {
        id: 'under-2',
        title: 'No Visible Damage',
        description: 'Look for scrapes, dents, or damage underneath',
        tip: 'Use phone camera to check if you cannot crawl under',
      },
      {
        id: 'under-3',
        title: 'Exhaust System',
        description: 'Check exhaust pipes are secure and undamaged',
        tip: 'No rust should be present on a new car',
      },
      {
        id: 'under-4',
        title: 'Fuel Tank',
        description: 'Inspect fuel tank for dents or damage',
        tip: 'Should be clean and undamaged',
      },
      {
        id: 'under-5',
        title: 'No Fluid Leaks',
        description: 'Check ground under car for any fluid spots',
        tip: 'Have car idle for a few minutes then check',
        critical: true,
      },
      {
        id: 'under-6',
        title: 'Suspension Components',
        description: 'Visually inspect visible suspension parts',
        tip: 'Look for any obvious damage or leaking shocks',
      },
    ],
  },
  {
    id: 'documents',
    title: 'Documents & Accessories',
    icon: 'FolderOpen',
    description: 'Verify all paperwork and included accessories',
    critical: true,
    items: [
      {
        id: 'doc-1',
        title: 'Invoice / Tax Invoice',
        description: 'Verify all details on invoice are correct',
        tip: 'Check price, variant, color, accessories listed',
        critical: true,
      },
      {
        id: 'doc-2',
        title: 'Insurance Policy',
        description: 'Verify insurance documents and policy details',
        tip: 'Check coverage amount, validity dates, IDV value',
        critical: true,
      },
      {
        id: 'doc-3',
        title: 'Warranty Card',
        description: 'Check warranty card with correct details filled',
        tip: 'Ensure VIN and date of sale are correctly entered',
        critical: true,
      },
      {
        id: 'doc-4',
        title: 'Service Booklet',
        description: 'Verify service booklet with maintenance schedule',
        tip: 'First free service details should be clear',
      },
      {
        id: 'doc-5',
        title: 'Owner\'s Manual',
        description: 'Check owner\'s manual is provided',
        tip: 'Physical book or pen drive with digital manual',
      },
      {
        id: 'doc-6',
        title: 'Temporary Registration',
        description: 'Verify temporary registration details',
        tip: 'Validity should be at least 30 days',
      },
      {
        id: 'doc-7',
        title: 'Form 21 & 22',
        description: 'Collect Form 21 (sale certificate) and Form 22 (roadworthiness)',
        tip: 'Essential for permanent registration',
        critical: true,
      },
      {
        id: 'doc-8',
        title: 'Number Plate (HSRP)',
        description: 'If HSRP applied, verify it\'s properly fitted',
        tip: 'Should be OEM quality with proper mounting',
      },
      {
        id: 'doc-9',
        title: 'All Key Sets',
        description: 'Collect all key sets - usually 2 smart keys + manual key',
        tip: 'Test both keys work for starting and locking',
        critical: true,
      },
      {
        id: 'doc-10',
        title: 'Accessories Installed',
        description: 'Verify all paid accessories are installed correctly',
        tip: 'Match against invoice line items',
      },
      {
        id: 'doc-11',
        title: 'First Aid Kit',
        description: 'Check first aid kit is provided (mandatory)',
        tip: 'Should contain basic medical supplies',
      },
      {
        id: 'doc-12',
        title: 'Warning Triangle',
        description: 'Verify warning/safety triangle is included',
        tip: 'Usually stored in boot/trunk area',
      },
      {
        id: 'doc-13',
        title: 'Fire Extinguisher',
        description: 'Check if fire extinguisher is provided',
        tip: 'Verify it\'s charged and not expired',
      },
    ],
  },
  {
    id: 'test-drive',
    title: 'Test Drive Checks',
    icon: 'Navigation',
    description: 'Essential checks during the test drive',
    items: [
      {
        id: 'td-1',
        title: 'Engine Start',
        description: 'Engine should start smoothly without unusual sounds',
        tip: 'Cold start may have brief elevated idle - this is normal',
        critical: true,
      },
      {
        id: 'td-2',
        title: 'Idle Quality',
        description: 'Engine should idle smoothly without vibration',
        tip: 'Rev meter should show stable RPM around 800-1000',
      },
      {
        id: 'td-3',
        title: 'Clutch Operation',
        description: 'Clutch should engage smoothly (manual transmission)',
        tip: 'Note the bite point - should be consistent',
      },
      {
        id: 'td-4',
        title: 'Gear Shifts',
        description: 'All gears should engage smoothly without grinding',
        tip: 'Test all gears including reverse',
        critical: true,
      },
      {
        id: 'td-5',
        title: 'Acceleration',
        description: 'Car should accelerate smoothly without hesitation',
        tip: 'No jerking or power loss during acceleration',
      },
      {
        id: 'td-6',
        title: 'Braking',
        description: 'Brakes should feel progressive and stop car straight',
        tip: 'Car should not pull to one side when braking',
        critical: true,
      },
      {
        id: 'td-7',
        title: 'Steering Feel',
        description: 'Steering should be smooth and return to center',
        tip: 'No unusual noises when turning',
      },
      {
        id: 'td-8',
        title: 'Straight Line Tracking',
        description: 'Car should drive straight without pulling',
        tip: 'Test on flat road - briefly release steering',
        critical: true,
      },
      {
        id: 'td-9',
        title: 'Suspension',
        description: 'No clunks or rattles over bumps',
        tip: 'Test over speed breakers at slow speed',
      },
      {
        id: 'td-10',
        title: 'No Warning Lights',
        description: 'No warning lights should appear during drive',
        tip: 'Check instrument cluster periodically during drive',
        critical: true,
      },
      {
        id: 'td-11',
        title: 'AC Performance',
        description: 'AC should cool cabin effectively during drive',
        tip: 'Test at various blower speeds',
      },
      {
        id: 'td-12',
        title: 'Noise Levels',
        description: 'No unusual wind, engine, or road noise',
        tip: 'Turn off AC/audio to listen for any odd sounds',
      },
      {
        id: 'td-13',
        title: 'Speedometer Accuracy',
        description: 'Compare speedometer to GPS speed if possible',
        tip: 'Small variation is normal (usually shows slightly higher)',
      },
    ],
  },
];

/**
 * Get all checklist items flattened (including feature-based items if features provided)
 */
export const getAllItems = (selectedFeatures = []) => {
  // Get standard checklist items
  const standardItems = CHECKLIST_SECTIONS.flatMap(section => 
    section.items.map(item => ({
      ...item,
      sectionId: section.id,
      sectionTitle: section.title,
    }))
  );
  
  // Get feature-based items
  const featureItems = selectedFeatures.flatMap(featureId => {
    const feature = FEATURE_BASED_CHECKS[featureId];
    if (!feature) return [];
    return feature.items.map(item => ({
      ...item,
      sectionId: `feature-${featureId}`,
      sectionTitle: feature.name,
      isFeatureItem: true,
    }));
  });
  
  return [...standardItems, ...featureItems];
};

/**
 * Get critical items that should not be failed
 */
export const getCriticalItems = (selectedFeatures = []) => {
  return getAllItems(selectedFeatures).filter(item => item.critical);
};

/**
 * Calculate section progress
 */
export const calculateSectionProgress = (sectionId, itemStatuses) => {
  const section = CHECKLIST_SECTIONS.find(s => s.id === sectionId);
  if (!section) return { completed: 0, total: 0, percentage: 0 };
  
  const total = section.items.length;
  const completed = section.items.filter(
    item => itemStatuses[item.id] && itemStatuses[item.id] !== 'pending'
  ).length;
  
  return {
    completed,
    total,
    percentage: Math.round((completed / total) * 100),
  };
};

/**
 * Calculate overall PDI progress (including feature-based items)
 */
export const calculateOverallProgress = (itemStatuses, selectedFeatures = []) => {
  const allItems = getAllItems(selectedFeatures);
  const total = allItems.length;
  const completed = allItems.filter(
    item => itemStatuses[item.id] && itemStatuses[item.id] !== 'pending'
  ).length;
  
  return {
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

/**
 * Calculate PDI score breakdown (including feature-based items)
 */
export const calculatePDIScore = (itemStatuses, selectedFeatures = []) => {
  const allItems = getAllItems(selectedFeatures);
  const counts = {
    total: allItems.length,
    checked: 0,
    pass: 0,
    fail: 0,
    attention: 0,
    pending: 0,
    criticalFails: [],
  };
  
  allItems.forEach(item => {
    const status = itemStatuses[item.id] || 'pending';
    
    if (status === 'pending') {
      counts.pending++;
    } else {
      counts.checked++;
      counts[status]++;
      
      if (status === 'fail' && item.critical) {
        counts.criticalFails.push(item);
      }
    }
  });
  
  // Score calculation: pass = 1, attention = 0.5, fail = 0
  const maxScore = counts.checked;
  const actualScore = counts.pass + (counts.attention * 0.5);
  counts.scorePercentage = maxScore > 0 ? Math.round((actualScore / maxScore) * 100) : 0;
  
  return counts;
};

/**
 * Get failed and attention items (including feature-based items)
 */
export const getIssueItems = (itemStatuses, notes, selectedFeatures = []) => {
  const allItems = getAllItems(selectedFeatures);
  
  return allItems
    .filter(item => {
      const status = itemStatuses[item.id];
      return status === 'fail' || status === 'attention';
    })
    .map(item => ({
      ...item,
      status: itemStatuses[item.id],
      note: notes[item.id] || '',
    }));
};

export default CHECKLIST_SECTIONS;
